import { api, extractListItems, extractPaging, extractResponsePaging, normalizePaging, type ApiResponse } from '../client';

// ================== TYPE DEFINITIONS ==================

// Employer Data Types
export interface Employer {
  uuid: string;
  name: string;
  abn?: string;
  contact_name: string;
  contact_email: string;
  contact_phone?: string;
  head_office_location?: string;
  /** API returns approved | unapproved | rej; display mapping uses verified | pending | rejected */
  status?: 'approved' | 'unapproved' | 'rejected' | 'verified' | 'pending' | 'seeded';
  employee_id?: string; // Generated field for displaySs
}

export interface PagingInfo {
  current_page: number;
  last_page: number;
  per_page: number;
  current_items: number;
  from: number;
  to: number;
  total: number;
}

export interface EmployersListResponse {
  success: boolean;
  data: Employer[];
  paging: PagingInfo;
  errors: any[];
}

// Archive Response Types
export interface ArchiveResponse {
  success: boolean;
  message: string;
  data: [];
}

export interface ArchiveErrorResponse {
  success: false;
  message: string;
  data: [];
}

// Invitation Request Types
export interface InviteEmployerRequest {
  name: string;
  contact_name: string;
  contact_email: string;
  contact_phone?: string;
  website?: string;
  reason_for_request?: string;
  head_office_location?: string;
}

// Response Types
export interface InvitationResponse {
  success: boolean;
  message: string;
  data: {
    invitation_uuid?: string;
    expires_at?: string;
  };
}



export interface InvitationErrorResponse {
  success: false;
  message: string;
  data: {
    errors?: {
      [key: string]: string[];
    };
  } | [];
}

export interface ErrorResponse {
  success: false;
  message: string;
  data?: any;
}

// ================== SERVICE CLASS ==================

export class MyEmployerService {

  /**
   * Get list of employers for a specific user
   * 
   * @param userUuid User UUID to fetch employers for
   * @param options Optional pagination and filter parameters
   * @returns Promise with employers list response
   */
  static async getEmployers(userUuid: string, options?: {
    page?: number;
    per_page?: number;
    only_archived?: boolean;
    search?: string;
  }): Promise<{
    success: boolean;
    message: string;
    data?: Employer[];
    paging?: PagingInfo;
    errors?: any;
  }> {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (options?.page) {
        params.append('page', options.page.toString());
      }
      if (options?.per_page) {
        params.append('per_page', options.per_page.toString());
      }
      if (options?.only_archived !== undefined) {
        params.append('only_archived', options.only_archived.toString());
      }
      if (options?.search) {
        params.append('search', options.search);
      }
      
      const queryString = params.toString();
      const url = `/users/${userUuid}/employers${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get<EmployersListResponse>(url);
      
      if (response.data.success) {
        const employers = extractListItems<Employer>(response.data.data);
        const paging = normalizePaging(
          extractResponsePaging(response) ?? extractPaging(response.data.data),
          employers.length
        ) as PagingInfo | undefined;
        return {
          success: true,
          message: 'Employers loaded successfully',
          data: employers,
          paging
        };
      } else {
        return {
          success: false,
          message: 'Failed to load employers',
          errors: response.data.errors
        };
      }
    } catch (error: any) {
      if (error.response?.data) {
        const errorData = error.response.data as ErrorResponse;
        return {
          success: false,
          message: errorData.message,
          errors: errorData.data
        };
      } else {
        return {
          success: false,
          message: error.message || 'Failed to fetch employers',
          errors: { general: [error.message || 'Network error'] }
        };
      }
    }
  }

  static async getAllEmployers(userUuid: string, options?: {
    page?: number;
    per_page?: number;
    only_archived?: boolean;
  }): Promise<{
    success: boolean;
    message: string;
    data?: Employer[];
    paging?: PagingInfo;
    errors?: any;
  }> {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (options?.page) {
        params.append('page', options.page.toString());
      }
      if (options?.per_page) {
        params.append('per_page', options.per_page.toString());
      }
      if (options?.only_archived !== undefined) {
        params.append('only_archived', options.only_archived.toString());
      }
      
      const queryString = params.toString();
      const url = `/resource-lists?types=employer${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get<EmployersListResponse>(url);
      
      if (response.data.success) {
        const employers = extractListItems<Employer>(response.data.data);
        const paging = normalizePaging(
          extractResponsePaging(response) ?? extractPaging(response.data.data),
          employers.length
        ) as PagingInfo | undefined;
        return {
          success: true,
          message: 'Employers loaded successfully',
          data: employers,
          paging
        };
      } else {
        return {
          success: false,
          message: 'Failed to load employers',
          errors: response.data.errors
        };
      }
    } catch (error: any) {
      if (error.response?.data) {
        const errorData = error.response.data as ErrorResponse;
        return {
          success: false,
          message: errorData.message,
          errors: errorData.data
        };
      } else {
        return {
          success: false,
          message: error.message || 'Failed to fetch employers',
          errors: { general: [error.message || 'Network error'] }
        };
      }
    }
  }
  
  /**
   * Invite an employer organization to join the platform
   * 
   * @param request Employer invitation data
   * @returns Promise with invitation response
   */
  static async inviteEmployer(request: InviteEmployerRequest): Promise<{
    success: boolean;
    message: string;
    data?: any;
    errors?: any;
  }> {
    try {
      const response = await api.post<InvitationResponse>('/user-invitation/invite-employer', request);
      
      if (response.data.success) {
        return {
          success: true,
          message: response.data.message,
          data: response.data.data
        };
      } else {
        return {
          success: false,
          message: response.data.message,
          errors: response.data.data
        };
      }
    } catch (error: any) {
      if (error.response?.data) {
        const errorData = error.response.data as InvitationErrorResponse;
        return {
          success: false,
          message: errorData.message,
          errors: errorData.data
        };
      } else {
        return {
          success: false,
          message: error.message,
          errors: { general: [error.message] }
        };
      }
    }
  }

  /**
   * Archive an employer for a specific user
   * 
   * @param userUuid User UUID
   * @param employerUuid Employer UUID to archive
   * @returns Promise with archive response
   */
  static async archiveEmployer(userUuid: string, employerUuid: string): Promise<{
    success: boolean;
    message: string;
    errors?: any;
  }> {
    try {
      const response = await api.put<ArchiveResponse>(`/users/${userUuid}/employers/${employerUuid}/archive`);
      
      if (response.data.success) {
        return {
          success: true,
          message: response.data.message
        };
      } else {
        return {
          success: false,
          message: response.data.message
        };
      }
    } catch (error: any) {
      if (error.response?.data) {
        const errorData = error.response.data as ArchiveErrorResponse;
        return {
          success: false,
          message: errorData.message,
          errors: errorData.data
        };
      } else {
        return {
          success: false,
          message: error.message || 'Failed to archive employer',
          errors: { general: [error.message || 'Network error'] }
        };
      }
    }
  }

  /**
   * Unarchive an employer for a specific user
   * 
   * @param userUuid User UUID
   * @param employerUuid Employer UUID to unarchive
   * @returns Promise with unarchive response
   */
  static async unarchiveEmployer(userUuid: string, employerUuid: string): Promise<{
    success: boolean;
    message: string;
    errors?: any;
  }> {
    try {
      const response = await api.put<ArchiveResponse>(`/users/${userUuid}/employers/${employerUuid}/unarchive`);
      
      if (response.data.success) {
        return {
          success: true,
          message: response.data.message
        };
      } else {
        return {
          success: false,
          message: response.data.message
        };
      }
    } catch (error: any) {
      if (error.response?.data) {
        const errorData = error.response.data as ArchiveErrorResponse;
        return {
          success: false,
          message: errorData.message,
          errors: errorData.data
        };
      } else {
        return {
          success: false,
          message: error.message || 'Failed to unarchive employer',
          errors: { general: [error.message || 'Network error'] }
        };
      }
    }
  }

  /**
   * Link user to an existing employer
   * 
   * @param userUuid User UUID
   * @param employerUuid Employer UUID to link
   * @returns Promise with link response
   */
  static async linkToExistingEmployer(userUuid: string, employerUuid: string): Promise<{
    success: boolean;
    message: string;
    data?: any;
    errors?: any;
  }> {
    try {
      const response = await api.post<ApiResponse<unknown>>(`/users/${userUuid}/employers/${employerUuid}`, {
        user_uuid: userUuid,
        employer_uuid: employerUuid
      });
      
      if (response.data.success) {
        return {
          success: true,
          message: response.data.message || 'Successfully linked to employer',
          data: response.data.data
        };
      } else {
        return {
          success: false,
          message: response.data.message || 'Failed to link employer',
          errors: response.data.data
        };
      }
    } catch (error: any) {
      if (error.response?.data) {
        const errorData = error.response.data;
        return {
          success: false,
          message: errorData.message || 'Failed to link employer',
          errors: errorData.data
        };
      } else {
        return {
          success: false,
          message: error.message || 'Failed to link employer',
          errors: { general: [error.message || 'Network error'] }
        };
      }
    }
  }

  /**
   * Search all employers (not just user's employers)
   * 
   * @param userUuid User UUID (required for API endpoint)
   * @param searchQuery Search query string
   * @returns Promise with employers list response
   */
  static async searchAllEmployers(userUuid: string, searchQuery: string): Promise<{
    success: boolean;
    message: string;
    data?: Employer[];
    errors?: any;
  }> {
    try {
      const response = await api.get<EmployersListResponse>(`/users/${userUuid}/employers?search=${encodeURIComponent(searchQuery)}&per_page=20`);
      
      if (response.data.success) {
        const employers = extractListItems<Employer>(response.data.data);
        return {
          success: true,
          message: 'Employers loaded successfully',
          data: employers
        };
      } else {
        return {
          success: false,
          message: 'Failed to load employers',
          errors: response.data.errors
        };
      }
    } catch (error: any) {
      if (error.response?.data) {
        const errorData = error.response.data as ErrorResponse;
        return {
          success: false,
          message: errorData.message,
          errors: errorData.data
        };
      } else {
        return {
          success: false,
          message: error.message || 'Failed to search employers',
          errors: { general: [error.message || 'Network error'] }
        };
      }
    }
  }

} 